import { lazy, Suspense } from 'react';
import {
  RouterProvider,
  createRouter,
  createRootRoute,
  createRoute,
} from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { AppErrorFallback } from './components/ui';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { API_ERROR_CODES } from '@arcanum-hq/shared-types';
import { triggerProtocolReload } from '@/lib/protocol-reload';
import { RouteErrorComponent } from '@/lib/route-error-component';
import { AuthProvider } from '@/hooks/use-auth';
import { PaymentRequiredProvider } from '@/contexts/PaymentRequiredContext';
import { SentryPostHogIdentity } from './components/SentryPostHogIdentity';
import { DesktopOnlyGate } from './components/DesktopOnlyGate';
import { RootLayout } from '@/routes/__root';
import { LoginPage } from '@/routes/login';
import { CockpitPage } from '@/routes/cockpit';
import { BuilderPage, builderSearchSchema } from '@/routes/builder';
// Solver hidden 2026-05-15 per user direction — page + components stay in
// place at `apps/web/src/routes/solver.tsx` + `apps/web/src/components/solver/*`
// + `apps/web/src/hooks/use-solver.ts` so re-enabling is just uncommenting
// this import + `solverRoute` + the route-tree entry + the Sidebar NAV_ITEMS
// row. No code deleted.
// import { SolverPage } from '@/routes/solver';
import { PipelinePage, pipelineSearchSchema } from '@/routes/pipeline';
import { SettingsPage, settingsSearchSchema } from '@/routes/settings';
import { SignupPage } from '@/routes/signup';
import { ForgotPasswordPage } from '@/routes/forgot-password';
import { AdminPage, adminSearchSchema } from '@/routes/admin';
import { ResetPasswordPage } from '@/routes/reset-password';
import { DealPage } from '@/routes/deal';
import { SharesPage, sharesSearchSchema } from '@/routes/shares';
import {
  AuthCallbackPage,
  authCallbackSearchSchema,
} from '@/routes/auth/callback';
import {
  BillingCheckoutPage,
  billingCheckoutSearchSchema,
} from '@/routes/billing/checkout';

// Route tree — all pages registered
const rootRoute = createRootRoute({ component: RootLayout });
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
});
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});
// `/` is a redirect-only route — unauth users hit /login (via __root.tsx
// auth-guard), authed users get bounced to /cockpit (the canonical
// dashboard URL). The actual cockpit lives at /cockpit so it's
// bookmark-friendly + URL-shareable.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => null, // __root.tsx auth-guard handles the redirect
});
const cockpitRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cockpit',
  component: CockpitPage,
});
const builderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/builder',
  component: BuilderPage,
  validateSearch: (search) => builderSearchSchema.parse(search),
});
// Solver route hidden 2026-05-15 — see import-block note above.
// const solverRoute = createRoute({ getParentRoute: () => rootRoute, path: '/solver', component: SolverPage });
const pipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pipeline',
  component: PipelinePage,
  validateSearch: (search) => pipelineSearchSchema.parse(search),
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
  validateSearch: (search) => settingsSearchSchema.parse(search),
});
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminPage,
  validateSearch: (search) => adminSearchSchema.parse(search),
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});
// Public deal-link surface — `/deal/:shareId`. Unauthenticated route;
// the __root.tsx auth-guard explicitly skips the auth-redirect for any
// pathname starting with `/deal/`. SHIPPED `agent-ux-optimization` Phase 6
// Session 6 (2026-05-08).
const dealRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/deal/$shareId',
  component: DealPage,
});
// `/shares` — deal-link inbox (Sent + Received tabs). Placeholder body in
// Phase 3 Session 3.1; Sent tab fills in 3.3, Received tab fills in Phase 4
// Session 4.1.
const sharesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shares',
  component: SharesPage,
  validateSearch: (search) => sharesSearchSchema.parse(search),
});
// `/auth/callback` — Supabase invite-redirect bridge. Recipients land here
// after consuming a magic-link invite (`${APP_URL}/auth/callback?share=<id>`),
// the route waits for `useAuth()` to resolve, then POSTs to
// `/api/auth/callback/share-link` and navigates to `/deal/<primaryShareId>`.
// Public route — bypasses the __root.tsx auth-guard via the `/auth/callback`
// branch in `isPublicRoute()`. SHIPPED `external-sharing` Phase 5 Session 5.1
// (2026-05-09).
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
  validateSearch: (search) => authCallbackSearchSchema.parse(search),
});
// `/billing/checkout` — approved + non-admin users who don't yet have a
// `usage_balance` row (= no active subscription) get redirected here by the
// __root.tsx cascade. Renders the tier grid by default; switches to the
// success-polling spinner when `?session_id=` arrives via Stripe redirect.
// SHIPPED `signup-billing-mvp` Phase 5 Session 7 (2026-05-13).
const billingCheckoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/checkout',
  component: BillingCheckoutPage,
  validateSearch: (search) => billingCheckoutSearchSchema.parse(search),
});

// DEV-only `ui-designer` prototype harness — mounted at `/prototype` +
// `/prototype/$slug`. The routes are registered STATICALLY (concretely typed,
// not via an `AnyRoute[]` spread) so the router's typed search/param inference
// stays intact for every real route. DEV-gating happens at the import +
// component level: the `import.meta.env.DEV ? lazy(import(...)) : null` ternary
// folds to `null` in production (Vite evaluates `import.meta.env.DEV` to a
// literal `false`), so the registry chunk + every prototype it pulls in are
// dead-code-eliminated and the route components render nothing. Same DCE
// discipline as the PROD-gated Sentry/PostHog init. `__root.helpers.ts`'s
// `isPublicRoute` returns true for `/prototype` (also DEV-gated) so prototypes
// render full-bleed without auth or a backend. See
// `apps/web/src/prototypes/registry.tsx` + `.claude/agents/ui-designer.md`.
const PrototypeIndexLazy = import.meta.env.DEV
  ? lazy(() =>
      import('@/prototypes/registry').then((m) => ({
        default: m.PrototypeIndex,
      }))
    )
  : null;
const PrototypeHostLazy = import.meta.env.DEV
  ? lazy(() =>
      import('@/prototypes/registry').then((m) => ({
        default: m.PrototypeHost,
      }))
    )
  : null;
function PrototypeIndexRoute() {
  if (!PrototypeIndexLazy) return null;
  return (
    <Suspense fallback={<div className="min-h-screen bg-void" />}>
      <PrototypeIndexLazy />
    </Suspense>
  );
}
function PrototypeHostRoute() {
  if (!PrototypeHostLazy) return null;
  return (
    <Suspense fallback={<div className="min-h-screen bg-void" />}>
      <PrototypeHostLazy />
    </Suspense>
  );
}
const prototypeIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prototype',
  component: PrototypeIndexRoute,
});
const prototypeSlugRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prototype/$slug',
  component: PrototypeHostRoute,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  cockpitRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  builderRoute,
  /* solverRoute — hidden 2026-05-15 */ pipelineRoute,
  settingsRoute,
  adminRoute,
  dealRoute,
  sharesRoute,
  authCallbackRoute,
  billingCheckoutRoute,
  prototypeIndexRoute,
  prototypeSlugRoute,
]);
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Every route inherits this fallback unless it overrides `errorComponent`.
  // RouteErrorComponent captures to Sentry (`surface: 'route-error'`) before
  // rendering AppErrorFallback. Router-internal throws (validateSearch /
  // loader / render) are NOT seen by the root <Sentry.ErrorBoundary> in App,
  // so this is the only Sentry path for those errors.
  defaultErrorComponent: RouteErrorComponent,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      // 2026-05-27 — idle-tab-reconnect hardening. Pre-fix every
      // `refetchOnWindowFocus: true` per-hook opt-in fired in the same JS
      // macrotask on tab resume, producing a 9-10 req burst that blew the
      // global 500/15min limiter. `<FocusResumeCoordinator>` (mounted in
      // __root.tsx) now invalidates the auth-cascade queries on resume + a
      // small deferred Tier-2 set; everything else relies on staleTime +
      // route-level navigation refetches. Hooks that still need focus
      // refetch keep their per-hook `refetchOnWindowFocus: true` (those
      // override this default).
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
  // builder-ssot-consolidation Phase 5 Session 5.8 (ADR 0019 Decision A.2):
  // Global 426 detector — any query error with PROTOCOL_VERSION_REQUIRED
  // triggers a once-per-tab reload so the client picks up the new bundle.
  // The sessionStorage guard in triggerProtocolReload prevents reload-loops.
  queryCache: new QueryCache({
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 426 &&
        err.code === API_ERROR_CODES.PROTOCOL_VERSION_REQUIRED
      ) {
        triggerProtocolReload();
      }
    },
  }),
});

export function App() {
  return (
    <DesktopOnlyGate>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SentryPostHogIdentity />{' '}
          {/* ADR 0016 — sibling consumer, under the provider */}
          <PaymentRequiredProvider>
            {/* Phase 3 § 3.2 — root <Sentry.ErrorBoundary> per ADR 0001.
                Mounted INSIDE <PaymentRequiredProvider> so <AppErrorFallback>
                retains access to the auth + payment-required contexts if it
                ever wants to display contextual info. */}
            <Sentry.ErrorBoundary
              fallback={({ resetError }) => (
                <AppErrorFallback onReset={resetError} />
              )}
            >
              <RouterProvider router={router} />
            </Sentry.ErrorBoundary>
          </PaymentRequiredProvider>
        </AuthProvider>
      </QueryClientProvider>
    </DesktopOnlyGate>
  );
}
